import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const RileyPark = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.riley-park-vancouver:About Riley Park Paragraph">
          Riley Park, often referred to as Riley Park–Little Mountain, is a
          vibrant and diverse neighborhood located in the heart of{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          . Bounded by 41st Avenue to the south, 16th Avenue to the north,
          Cambie Street to the west, and Fraser Street to the east, this area
          offers a unique blend of urban convenience and suburban tranquility.
          The neighborhood is characterized by its leafy streets, historic
          homes, and a strong sense of community, making it a desirable place to
          live for families, young professionals, and retirees alike.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,400 - $2,700',
          bed2: '$3,300 - $3,700',
          bed3: '$4,200 - $4,800'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.riley-park-vancouver:Apartment Hunting Riley Park Paragraph 1">
            Finding an apartment for rent in Riley Park, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Riley Park lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.riley-park-vancouver:Apartment Hunting Riley Park Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Apartments for rent in Riley Park Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Riley Park, Vancouver
              </LinkText>
              , cater to a diverse range of residents, including young
              professionals and small families. These units often feature modern
              designs and are situated within walking distance to local shops,
              cafes, and public transit. Renting an apartment in Riley Park
              offers the advantage of residing in a community-oriented
              neighborhood with easy access to downtown Vancouver.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Houses for rent in Riley Park Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Riley Park, Vancouver
              </LinkText>
              , are ideal for families and individuals seeking spacious living
              in a central location. These homes often boast charming
              architectural details and private yards. Renting a house in this
              neighborhood provides the benefit of a peaceful environment,
              proximity to reputable schools, and access to numerous parks and
              recreational facilities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Townhouses for rent in Riley Park Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Riley Park, Vancouver
              </LinkText>{' '}
              , offer a balance between apartment living and detached homes.
              These properties typically provide multiple bedrooms and modern
              amenities, appealing to professionals and families seeking a
              comfortable urban lifestyle. Renting a townhouse here allows
              residents to enjoy the neighborhood’s vibrant atmosphere and
              close-knit community feel.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Basements for rent in Riley Park Paragraph">
              Basements for rent in Riley Park, Vancouver, present affordable
              housing options for students and young professionals. These units
              vary in size and amenities, often featuring private entrances and
              essential appliances. Renting a basement suite in this
              neighborhood offers the advantage of living in a safe,
              community-focused area with excellent transit connections.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Condos for rent Riley Park Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Riley Park, Vancouver
              </LinkText>
              , appeal to individuals and couples looking for contemporary
              living spaces with amenities like fitness centers and communal
              areas. These units often feature sleek designs and are situated
              close to shopping districts and parks. Renting a condo here
              provides a low-maintenance lifestyle in a desirable neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Rooms for rent in Riley Park Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Riley Park, Vancouver
              </LinkText>{' '}
              , are ideal for students and single professionals seeking
              cost-effective housing. These accommodations typically involve
              shared common areas and provide a community living experience.
              Renting a room in this neighborhood allows for affordable living
              in a vibrant and attractive area.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.riley-park-vancouver:Short term rentals in Riley Park Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Riley Park, Vancouver
              </LinkText>
              , are suitable for tourists, business travelers, or individuals in
              transition. These accommodations range from private rooms to
              entire homes, offering flexibility and comfort. Renting short-term
              in this neighborhood provides a convenient base while exploring
              Vancouver’s attractions and amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.riley-park-vancouver:Exploring Riley Park Paragraph">
          Riley Park is a neighborhood that beautifully balances urban amenities
          with natural beauty. At its heart lies{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/queen-elizabeth-park-what-to-see.aspx">
            Queen Elizabeth Park
          </LinkText>
          , Vancouver’s highest point, offering stunning city views,
          meticulously maintained gardens, and recreational facilities such as
          tennis courts and a pitch-and-putt golf course. The{' '}
          <LinkText href="http://www.bloedelconservatory.com/">
            Bloedel Conservatory
          </LinkText>
          , located within the park, houses a vibrant collection of exotic
          plants and free-flying tropical birds. The neighborhood’s main
          commercial thoroughfare,{' '}
          <LinkText href="https://govancity.com/vancouver-shopping-districts/main-street/">
            Main Street
          </LinkText>
          , is lined with an eclectic mix of boutiques, antique shops, cafes,
          and restaurants, reflecting the area’s diverse cultural fabric. Riley
          Park also hosts the popular{' '}
          <LinkText href="https://eatlocal.org/markets/riley-park/">
            Riley Park Farmers Market
          </LinkText>
          , where locals gather to purchase fresh produce and artisanal goods,
          fostering a strong sense of community.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.riley-park-vancouver:Things To Do Riley Park Paragraph">
          Riley Park offers a plethora of activities catering to various
          interests. Nature enthusiasts can explore the lush landscapes of{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/queen-elizabeth-park-what-to-see.aspx">
            Queen Elizabeth Park
          </LinkText>
          , which features the{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/queen-elizabeth-park-what-to-see.aspx">
            Quarry Garden
          </LinkText>
          —a transformed former rock quarry showcasing a stunning array of
          plants and flowers. Sports fans can catch a Vancouver Canadians
          baseball game at the historic{' '}
          <LinkText href="https://www.milb.com/vancouver/ballpark">
            Nat Bailey Stadium
          </LinkText>
          , a cherished local landmark. For those interested in community
          events, the{' '}
          <LinkText href="https://eatlocal.org/markets/riley-park/">
            Riley Park Farmers Market
          </LinkText>{' '}
          operates year-round, providing access to fresh, local produce and
          handcrafted items. Art lovers will appreciate the neighborhood’s
          numerous galleries and public art installations, while foodies can
          indulge in the diverse culinary offerings along{' '}
          <LinkText href="https://govancity.com/vancouver-shopping-districts/main-street/">
            Main Street
          </LinkText>
          , ranging from trendy cafes to international cuisines. With its blend
          of recreational spaces, cultural venues, and dining options, Riley
          Park caters to both residents and visitors seeking a dynamic yet
          relaxed urban experience.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.riley-park-vancouver:Restaurants Riley Park Paragraph">
        <Paragraph>
          Riley Park Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.theacornrestaurant.ca/">
            The Acorn
          </LinkText>{' '}
          - The Acorn is an award-winning vegetarian restaurant renowned for its
          innovative plant-based dishes that appeal to both vegetarians and
          omnivores. The menu showcases seasonal, locally sourced ingredients
          crafted into artful creations. The minimalist yet cozy ambiance,
          complemented by an extensive wine and cocktail list, makes it ideal
          for intimate dinners and special occasions. This restaurant caters to
          those seeking a refined dining experience with a focus on
          sustainability.
        </Paragraph>
        <Paragraph>
          2) <LinkText href="https://www.eastiseast.ca/">East is East</LinkText>{' '}
          - East is East offers a unique fusion of Middle Eastern and South
          Asian cuisines, providing a rich tapestry of flavors. The menu
          features an array of dishes inspired by the ancient Silk Road,
          including hearty stews, aromatic curries, and freshly baked roti. The
          warm, eclectic decor and live music performances create an immersive
          dining atmosphere.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.vancouverdine.com/seasons/">
            Seasons in the Park
          </LinkText>{' '}
          - Nestled within Queen Elizabeth Park, Seasons in the Park offers
          diners panoramic views of Vancouver’s skyline and the North Shore
          Mountains. The restaurant specializes in West Coast cuisine, with a
          menu highlighting fresh, locally sourced ingredients. The elegant
          setting, combined with exceptional service, makes it a favorite
          destination for romantic dinners and celebratory events.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.riley-park-vancouver:Transportation Riley Park Paragraph">
          Getting around Riley Park, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Riley Park are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Riley Park offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.riley-park-vancouver:Where To Stay Riley Park Paragraph">
          Accommodation options in Riley Park, Vancouver cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Riley Park itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Riley Park
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default RileyPark
